import type { FC } from 'react';
import { CircleHelp, PencilLine } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';
import {
  ArtifactCard,
  ArtifactMeta,
  PrimaryButton,
  SecondaryButton,
} from './procurement-parts.tsx';

const messages = defineMessages({
  kind: {
    id: 'brief.kind',
    defaultMessage: 'Procurement brief',
    description: 'Eyebrow label marking the brief as a formal artifact.',
  },
  reference: {
    id: 'brief.meta.reference',
    defaultMessage: 'Reference',
    description: 'Label of the brief reference line in the document header.',
  },
  preparedBy: {
    id: 'brief.meta.preparedBy',
    defaultMessage: 'Prepared by',
    description: 'Label of the prepared-by line in the document header.',
  },
  preparer: {
    id: 'brief.meta.preparer',
    defaultMessage: 'Shivi (AI Procurement Guide)',
    description: 'Who prepared the brief, shown when no buyer company is known.',
  },
  preparerFor: {
    id: 'brief.meta.preparerFor',
    defaultMessage: 'Shivi (AI Procurement Guide) for {company}',
    description: 'Who prepared the brief and for which buyer company.',
  },
  date: {
    id: 'brief.meta.date',
    defaultMessage: 'Date',
    description: 'Label of the date line in the document header.',
  },
  assumptionsHint: {
    id: 'brief.assumptionsHint',
    defaultMessage: 'Correct me on any of these',
    description: 'Caption under the assumptions section heading.',
  },
  openQuestions: {
    id: 'brief.openQuestions',
    defaultMessage: 'Still open',
    description: 'Heading above questions that remain unanswered but do not block matching.',
  },
  confirm: {
    id: 'brief.confirm',
    defaultMessage: 'Confirm brief',
    description: 'Button that confirms the brief and starts supplier matching.',
  },
  revise: {
    id: 'brief.revise',
    defaultMessage: 'Change something',
    description: 'Button that reopens the brief for edits.',
  },
  statusLabel: {
    id: 'brief.status.label',
    defaultMessage: 'Status',
    description: 'Label of the status line at the foot of the brief.',
  },
  draft: {
    id: 'brief.status.draft',
    defaultMessage: 'Draft',
    description: 'Status shown while the buyer has not yet confirmed the brief.',
  },
  confirmed: {
    id: 'brief.status.confirmed',
    defaultMessage: 'Confirmed by buyer',
    description: 'Status shown once the buyer confirmed the brief.',
  },
});

/** The assumptions section is styled apart — it is the one part the buyer must audit. */
const ASSUMPTIONS_HEADING = 'Assumptions made by Shivi';

interface BriefField {
  label: string;
  value: string;
  caption?: string;
}

interface BriefSection {
  heading: string;
  fields: BriefField[];
  notes: string[];
}

function readFields(value: unknown): BriefField[] {
  return arr(value)
    .filter(
      (entry): entry is Record<string, unknown> =>
        typeof entry === 'object' && entry !== null && !Array.isArray(entry),
    )
    .map((entry) => {
      const caption = optStr(entry.caption);
      return {
        label: str(entry.label),
        value: str(entry.value),
        ...(caption ? { caption } : {}),
      };
    })
    .filter((entry) => entry.label.length > 0);
}

function readSections(value: unknown): BriefSection[] {
  return arr(value)
    .filter(
      (entry): entry is Record<string, unknown> =>
        typeof entry === 'object' && entry !== null && !Array.isArray(entry),
    )
    .map((entry) => ({
      heading: str(entry.heading),
      fields: readFields(entry.fields),
      notes: arr(entry.notes).map(str).filter(Boolean),
    }))
    .filter((entry) => entry.heading.length > 0 && (entry.fields.length > 0 || entry.notes.length > 0));
}

export const ProcurementBrief: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const dispatch = useSurfaceAction();
  const item = str(node.props.item);
  const reference = optStr(node.props.reference) ?? '';
  const buyerCompany = optStr(node.props.buyerCompany);
  const date = optStr(node.props.date) ?? '';
  const sections = readSections(node.props.sections);
  const openQuestions = arr(node.props.openQuestions).map(str).filter(Boolean);
  const confirmed = str(node.props.status) === 'confirmed';

  return (
    <ArtifactCard>
      <header className="border-b border-border pb-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-body-xs font-semibold uppercase tracking-caps text-brand-accent-ink">
            {intl.formatMessage(messages.kind)}
          </p>
          {confirmed ? (
            <span className="rounded-sm bg-success/10 px-2 py-1 text-body-xs font-semibold text-success">
              {intl.formatMessage(messages.confirmed)}
            </span>
          ) : null}
        </div>
        <h1 className="mt-2 text-balance font-display text-heading-lg font-bold tracking-tight text-foreground">
          {item}
        </h1>
        <div className="mt-3">
          <ArtifactMeta
            rows={[
              { label: intl.formatMessage(messages.reference), value: reference },
              {
                label: intl.formatMessage(messages.preparedBy),
                value: buyerCompany
                  ? intl.formatMessage(messages.preparerFor, { company: buyerCompany })
                  : intl.formatMessage(messages.preparer),
              },
              { label: intl.formatMessage(messages.date), value: date },
            ]}
          />
        </div>
      </header>

      <div className="flex flex-col gap-5">
        {sections.map((section, index) => {
          const isAssumptions = section.heading === ASSUMPTIONS_HEADING;
          return (
            <section
              key={section.heading}
              className={
                isAssumptions ? 'rounded-lg bg-muted p-4' : 'border-t border-border pt-4 first:border-t-0 first:pt-0'
              }
            >
              <h2 className="flex items-center gap-2 text-body-xs font-semibold uppercase tracking-caps text-muted-foreground">
                {isAssumptions ? (
                  <PencilLine aria-hidden="true" className="h-4 w-4 text-brand-accent" />
                ) : (
                  <span className="text-brand-accent-ink">{index + 1}</span>
                )}
                {section.heading}
              </h2>
              {isAssumptions ? (
                <p className="mt-1 text-body-xs text-muted-foreground-subtle">
                  {intl.formatMessage(messages.assumptionsHint)}
                </p>
              ) : null}

              {section.fields.length > 0 ? (
                <dl className="mt-2 flex flex-col divide-y divide-border">
                  {section.fields.map((field) => (
                    <div
                      key={field.label}
                      className="grid gap-1 py-2 sm:grid-cols-[minmax(0,11rem)_1fr] sm:gap-4"
                    >
                      <dt className="text-body-sm font-semibold text-muted-foreground">{field.label}</dt>
                      <dd className="min-w-0">
                        <p className="break-words text-body-base text-foreground">{field.value}</p>
                        {field.caption ? (
                          <p className="mt-0.5 text-body-xs text-muted-foreground-subtle">
                            {field.caption}
                          </p>
                        ) : null}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : null}

              {section.notes.length > 0 ? (
                <ul className="mt-2 flex flex-col gap-1.5">
                  {section.notes.map((note) => (
                    <li key={note} className="text-body-sm text-muted-foreground">
                      {note}
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          );
        })}
      </div>

      {openQuestions.length > 0 ? (
        <section>
          <h2 className="flex items-center gap-2 text-body-sm font-semibold text-foreground">
            <CircleHelp aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
            {intl.formatMessage(messages.openQuestions)}
          </h2>
          <ul className="mt-2 flex flex-col gap-1.5">
            {openQuestions.map((question) => (
              <li key={question} className="text-body-sm text-muted-foreground">
                {question}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <footer className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border pt-4 text-body-xs">
        <span className="font-semibold uppercase tracking-caps text-muted-foreground-subtle">
          {intl.formatMessage(messages.statusLabel)}
        </span>
        <span className={confirmed ? 'text-success' : 'text-foreground'}>
          {intl.formatMessage(confirmed ? messages.confirmed : messages.draft)}
        </span>
      </footer>

      {confirmed ? null : (
        <div className="flex flex-wrap gap-3">
          <PrimaryButton
            label={intl.formatMessage(messages.confirm)}
            onClick={() => dispatch?.('confirmBrief', {})}
          />
          <SecondaryButton
            label={intl.formatMessage(messages.revise)}
            onClick={() => dispatch?.('reviseBrief', {})}
          />
        </div>
      )}
    </ArtifactCard>
  );
};
